import { useCallback, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { MultiValue, SingleValue } from "react-select";
import { format } from "date-fns";
import { z } from "zod";
import { DateRange } from "react-day-picker";

import { OptionType } from "../../../../../../app/utils/optionSelectType";
import { TransactionSellerContext } from "../../../../../../app/contexts/TransactionSellerContext";
import { useCustomers } from "../../../../../../app/hooks/useCustomers";
import { useParams } from "react-router-dom";

const schema = z.object({
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  customerIds: z.array(z.string()).optional(),
  status: z.array(z.string()).optional(),
  type: z.string().optional(),
});

type FormFilterData = z.infer<typeof schema>;

export const useSellerTransactionFilter = () => {
  const { id } = useParams();
  const [selectedCustomerOptions, setSelectedCustomerOptions] = useState<
    MultiValue<OptionType>
  >([]);
  const [selectedStatusOptions, setSelectedStatusOptions] = useState<
    MultiValue<OptionType>
  >([]);
  const [selectedTypeOption, setSelectedTypeOption] =
    useState<OptionType | null>(null);
  const [selectedDate, setSelectedDate] = useState<DateRange | undefined>(
    undefined,
  );

  const { setFilterParams } = useContext(TransactionSellerContext);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormFilterData>({
    resolver: zodResolver(schema),
  });

  const {
    data: customerData,
    fetchNextPage: fetchCustomers,
    hasNextPage: hasMoreCustomer,
    isFetching: isFetchingCustomer,
    isLoading: isLoadingCustomer,
    setParams,
  } = useCustomers({ initialLimit: 10 });

  const transformedCustomersData = () => {
    if (!customerData) return [];
    const allcustomers = customerData.pages.flatMap((page) => page.customers);
    return allcustomers.map((customer) => ({
      value: customer._id,
      label: customer.name,
    }));
  };

  const handleLoadMoreCustomer = () => {
    if (hasMoreCustomer) {
      fetchCustomers();
    }
  };

  const handleSelectCustomer = (
    selectedCustomerOptions: MultiValue<OptionType>,
  ) => {
    setValue(
      "customerIds",
      selectedCustomerOptions?.map((option) => option.value.toString()) || [],
    );
    setSelectedCustomerOptions(selectedCustomerOptions);
  };

  const handleSelectStatus = (
    selectedStatusOptions: MultiValue<OptionType>,
  ) => {
    setValue(
      "status",
      selectedStatusOptions?.map((option) => option.value.toString()) || [],
    );
    setSelectedStatusOptions(selectedStatusOptions);
  };

  const handleSelectType = (selectedOption: SingleValue<OptionType>) => {
    setValue("type", selectedOption?.value.toString() || "");
    setSelectedTypeOption(selectedOption);
  };

  const handleClearFilter = () => {
    reset({
      startDate: "",
      endDate: "",
      type: "",
    });
    setSelectedCustomerOptions([]);
    setSelectedStatusOptions([]);
    setSelectedTypeOption(null);
    setSelectedDate(undefined);
  };

  const handleDateChange = (dateRange: DateRange | undefined) => {
    setSelectedDate(dateRange);
    if (dateRange?.from) {
      setValue("startDate", format(dateRange.from, "yyyy-MM-dd"));
    } else {
      setValue("startDate", "");
    }
    if (dateRange?.to) {
      setValue("endDate", format(dateRange.to, "yyyy-MM-dd"));
    } else {
      setValue("endDate", "");
    }
  };

  const handleCustomerFilterBySellerId = useCallback(() => {
    setParams({
      sellerId: id,
    });
  }, [id, setParams]);

  useEffect(() => {
    handleCustomerFilterBySellerId();
  }, [handleCustomerFilterBySellerId]);

  const handleFilterSubmit = handleSubmit(async (data) => {
    try {
      let endDatePlusOneDay;

      if (data.endDate) {
        endDatePlusOneDay = new Date(data.endDate);

        endDatePlusOneDay.setDate(endDatePlusOneDay.getDate() + 1);
      }

      const startDate = data.startDate
        ? format(new Date(data.startDate), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx")
        : undefined;
      const endDate = endDatePlusOneDay
        ? format(endDatePlusOneDay, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx")
        : undefined;

      let customerIds = "";
      let status = "";

      if (data.customerIds && data.customerIds?.length > 0) {
        customerIds = data.customerIds
          .map((customerId) => customerId)
          .join(",");
      }

      if (data.status && data.status?.length > 0) {
        status = data.status.map((status) => status).join(",");
      }

      const params = {
        startDate,
        endDate,
        customerIds: customerIds || undefined,
        status: status || undefined,
        sellerIds: id,
        type: data.type || undefined,
      };

      setFilterParams(params);
    } catch (error) {
      console.log(error);
    }
  });

  return {
    handleFilterSubmit,
    handleClearFilter,
    register,
    errors,
    isSubmitting,
    selectedCustomerOptions,
    selectedStatusOptions,
    handleSelectCustomer,
    handleSelectStatus,
    customerOptions: transformedCustomersData(),
    handleLoadMoreCustomer,
    isFetchingCustomer,
    isLoadingCustomer,
    selectedDate,
    handleDateChange,
    selectedTypeOption,
    handleSelectType,
  };
};
